import {
  Button,
  ButtonVariant,
  ColorClass,
  Icon,
  IconIdentifier,
  toast,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { isEmpty, isEqual, isNil, omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  useAddHeapClusterToDashboard,
  useDeleteFile,
  useUpdateHeapClusterDashboardConfiguration,
} from 'shared/api';
import { ConfirmationModal } from 'shared/components';
import { useAppSelector } from 'src/app/hooks';
import { selectMap3dDataset } from 'src/map-3d/shared/map-3d-slices';
import { DashboardAttachments } from './dashboard-attachments';
import { DashboardConfiguration } from './dashboard-configuration';
import {
  buildDashboardPayload,
  getDashboardDefaultValues,
  mapExistingAttachments,
} from './helpers';
import {
  type Attachment,
  type DashboardConfigurationType,
  type DashboardModalProps,
  DashboardModalStep,
} from './types';

export const DashboardModal: React.FC<DashboardModalProps> = ({
  onClose,
  selectedDashboardCluster,
  dashboardCategoryConfig,
  refetchHeapList,
}) => {
  // Selectors.
  const { selectedTerrainIteration } = useAppSelector(selectMap3dDataset);

  const { accountingMonth, accountingYear } = selectedDashboardCluster;

  const defaultAccountingMonth =
    isNil(accountingMonth) || isNil(accountingYear)
      ? (selectedTerrainIteration?.date?.date as Date)
      : new Date(accountingYear, accountingMonth - 1);

  const initialValues = getDashboardDefaultValues(
    selectedDashboardCluster,
    dashboardCategoryConfig,
    defaultAccountingMonth,
  );

  const initialAttachments = mapExistingAttachments(
    selectedDashboardCluster.attachments || [],
  );

  // States.
  const [currentStep, setCurrentStep] = useState<DashboardModalStep>(
    DashboardModalStep.Configuration,
  );

  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);

  const [dashboardFormValues, setDashboardFormValues] =
    useState<DashboardConfigurationType>(initialValues);

  const [attachments, setAttachments] =
    useState<Attachment[]>(initialAttachments);

  const [isLeadDistanceEnabled, setIsLeadDistanceEnabled] = useState(
    selectedDashboardCluster.includeLeadDistanceInfo,
  );

  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const [shouldCancelUploads, setShouldCancelUploads] = useState(false);

  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  // ClassNames.
  const customContentClassName = classNames({
    'dashboard-modal--overlayed': showUnsavedChangesModal,
  });

  // Api.
  const { mutate: sendDeleteFileRequest } = useDeleteFile();

  const {
    mutate: addHeapClusterToDashboard,
    isPending: isAddHeapClusterToDashboardLoading,
    isError: isAddHeapClusterToDashboardError,
    isSuccess: isAddHeapClusterToDashboardSuccess,
  } = useAddHeapClusterToDashboard();

  const {
    mutate: updateHeapClusterDashboardConfiguration,
    isPending: isUpdateHeapClusterDashboardConfigurationLoading,
    isError: isUpdateHeapClusterDashboardConfigurationError,
    isSuccess: isUpdateHeapClusterDashboardConfigurationSuccess,
  } = useUpdateHeapClusterDashboardConfiguration();

  // Effects.
  useEffect(() => {
    if (isAddHeapClusterToDashboardSuccess) {
      toast.success('Added to dashboard successfully.');
      handleCloseDashboardModal();
      refetchHeapList();
    }

    if (isAddHeapClusterToDashboardError) {
      toast.error('Failed to add to dashboard. Please try again.');
    }
  }, [isAddHeapClusterToDashboardSuccess, isAddHeapClusterToDashboardError]);

  useEffect(() => {
    if (isUpdateHeapClusterDashboardConfigurationSuccess) {
      toast.success('Record updated successfully.');
      handleCloseDashboardModal();
      refetchHeapList();
    }

    if (isUpdateHeapClusterDashboardConfigurationError) {
      toast.error('Failed to update record. Please try again.');
    }
  }, [
    isUpdateHeapClusterDashboardConfigurationSuccess,
    isUpdateHeapClusterDashboardConfigurationError,
  ]);

  // Variables.
  const isConfigurationStep = currentStep === DashboardModalStep.Configuration;

  const isEditMode = selectedDashboardCluster.includeInDashboard;

  const leftButtonLabel =
    isEditMode && isConfigurationStep ? 'Update & Close' : 'Cancel';

  const rightButtonLabel = isEditMode
    ? isUpdateHeapClusterDashboardConfigurationLoading
      ? 'Updating dashboard'
      : 'Update'
    : isAddHeapClusterToDashboardLoading
      ? 'Adding to dashboard'
      : 'Add to dashboard';

  // Strip the local-only `clientId` so card reordering/re-adds don't read as changes.
  const normalizeLeadDistances = (
    leadDistances: DashboardConfigurationType['leadDistances'],
  ) => leadDistances.map((entry) => omit(entry, 'clientId'));

  const hasConfigurationChanges = !isEqual(
    {
      ...dashboardFormValues,
      month: dashboardFormValues.accountingMonth?.getMonth(),
      year: dashboardFormValues.accountingMonth?.getFullYear(),
      accountingMonth: null,
      leadDistances: normalizeLeadDistances(dashboardFormValues.leadDistances),
      deletedLeadDistanceIds: dashboardFormValues.deletedLeadDistanceIds,
      includeLeadDistanceInfo: isEditMode ? isLeadDistanceEnabled : null,
    },
    {
      ...initialValues,
      month: initialValues.accountingMonth?.getMonth(),
      year: initialValues.accountingMonth?.getFullYear(),
      accountingMonth: null,
      leadDistances: normalizeLeadDistances(initialValues.leadDistances),
      deletedLeadDistanceIds: initialValues.deletedLeadDistanceIds,
      includeLeadDistanceInfo: isEditMode
        ? initialValues.includeLeadDistanceInfo
        : null,
    },
  );

  const hasAttachmentChanges =
    attachments.length !== initialAttachments.length ||
    attachments.some(
      (attachment) =>
        !initialAttachments.some(
          (initialAttachment) =>
            initialAttachment.fileUploadId === attachment.fileUploadId,
        ),
    );

  const hasUnsavedChanges = hasConfigurationChanges || hasAttachmentChanges;

  // Handlers.
  const handleNext = () => {
    setCurrentStep(DashboardModalStep.Attachments);
  };

  const handleBack = () => {
    setCurrentStep(DashboardModalStep.Configuration);
  };

  const getDashboardPayload = () => {
    return buildDashboardPayload({
      dashboardFormValues,
      selectedDashboardClusterId: selectedDashboardCluster.id,
      isLeadDistanceEnabled,
      attachments,
    });
  };

  const handleSubmit = () => {
    const payload = getDashboardPayload();

    if (isNil(payload)) {
      return;
    }

    addHeapClusterToDashboard(payload);
  };

  const handleUpdateAndClose = () => {
    const payload = getDashboardPayload();

    if (isNil(payload)) {
      return;
    }

    updateHeapClusterDashboardConfiguration({
      ...payload,
      id: selectedDashboardCluster.id,
    });
  };

  const handleCloseDashboardModal = () => {
    onClose();

    setIsLeadDistanceEnabled(false);
    setShowUnsavedChangesModal(false);
    setShouldCancelUploads(false);
  };

  const handleAttemptClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
      return;
    }

    handleCloseDashboardModal();
  };

  const handleForceCloseDashboardModal = async () => {
    const newlyUploadedFiles = attachments.filter(
      ({ fileUploadId, isExisting }) => !isExisting && !isNil(fileUploadId),
    );

    if (isEmpty(newlyUploadedFiles)) {
      handleCloseDashboardModal();
      return;
    }

    setShouldCancelUploads(true);

    const deletableFileIds = newlyUploadedFiles.map(
      ({ fileUploadId }) => fileUploadId as string,
    );

    await Promise.allSettled(
      deletableFileIds.map(
        (fileId) =>
          new Promise((resolve, reject) => {
            sendDeleteFileRequest(
              { id: fileId },
              {
                onSuccess: resolve,
                onError: reject,
              },
            );
          }),
      ),
    );

    handleCloseDashboardModal();
    setShouldCancelUploads(false);
  };

  return (
    <Modal
      className="dashboard-modal"
      contentClassName={customContentClassName}
      show
      centered
    >
      <Modal.Header>
        <div>{isEditMode ? 'Edit dashboard record' : 'Add to dashboard'}</div>

        <Icon
          identifier={IconIdentifier.Cross}
          className="btn m-0 p-0"
          onClick={handleAttemptClose}
          size={24}
          colorClass={ColorClass.Neutral300}
        />
      </Modal.Header>

      <Modal.Body>
        {isConfigurationStep && (
          <DashboardConfiguration
            selectedDashboardCluster={selectedDashboardCluster}
            dashboardFormValues={dashboardFormValues}
            setDashboardFormValues={setDashboardFormValues}
            setIsNextDisabled={setIsNextDisabled}
            isLeadDistanceEnabled={isLeadDistanceEnabled}
            setIsLeadDistanceEnabled={setIsLeadDistanceEnabled}
            dashboardCategoryConfig={dashboardCategoryConfig}
          />
        )}

        <DashboardAttachments
          attachments={attachments}
          setAttachments={setAttachments}
          handleBack={handleBack}
          currentStep={currentStep}
          shouldCancelUploads={shouldCancelUploads}
          setIsUploadingFiles={setIsUploadingFiles}
          isDropZoneDisabled={
            isAddHeapClusterToDashboardLoading ||
            isUpdateHeapClusterDashboardConfigurationLoading
          }
        />
      </Modal.Body>

      <Modal.Footer>
        {/* Left Buttons */}
        <Button
          variant={ButtonVariant.Secondary}
          onClick={
            isEditMode && isConfigurationStep
              ? handleUpdateAndClose
              : handleAttemptClose
          }
          isLoading={
            isEditMode && isConfigurationStep
              ? isUpdateHeapClusterDashboardConfigurationLoading
              : false
          }
          disabled={
            isEditMode && isConfigurationStep
              ? isUpdateHeapClusterDashboardConfigurationLoading ||
                isNextDisabled ||
                !hasUnsavedChanges
              : false
          }
        >
          {leftButtonLabel}
        </Button>

        {/* Right Buttons */}
        {isConfigurationStep ? (
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            Next
          </Button>
        ) : (
          <Button
            variant={ButtonVariant.Primary}
            onClick={isEditMode ? handleUpdateAndClose : handleSubmit}
            isLoading={
              isEditMode
                ? isUpdateHeapClusterDashboardConfigurationLoading
                : isAddHeapClusterToDashboardLoading
            }
            disabled={
              isEditMode
                ? isUpdateHeapClusterDashboardConfigurationLoading ||
                  !hasUnsavedChanges
                : isUploadingFiles
            }
          >
            {rightButtonLabel}
          </Button>
        )}
      </Modal.Footer>

      {/* Unsaved Changes Modal */}
      {showUnsavedChangesModal && (
        <ConfirmationModal
          title="Unsaved Changes Detected"
          message="Closing this will discard your unsaved changes. Do you want to continue?"
          confirmText="Yes, Close It"
          isConfirmDanger
          onClose={() => setShowUnsavedChangesModal(false)}
          onSubmit={handleForceCloseDashboardModal}
        />
      )}
    </Modal>
  );
};
